// prettier-ignore

import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  ColumnDef,
  VisibilityState,
} from '@tanstack/react-table';
import {
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  CircleStackIcon,
} from '@heroicons/react/24/outline';
import InvocationDetailDrawer from './InvocationDetailDrawer';
import EventDetailDrawer from './EventDetailDrawer';
import JobDetailDrawer from './JobDetailDrawer';
import { useInvocationsListQuery } from '../types/generated';
import { resolveTimeRange } from '../utils/resolveTimeRange';
import { Node } from 'reactflow';
import { formatRelativeTime } from '../utils/formatTime';

interface Invocation {
  id: string;
  sourceFunction: string;
  correlationId: string;
  userEmail: string;
  sourceOperation: string;
  sourceJobId?: string;
  totalDuration: number;
  eventsDetectedCount: number;
  eventsUndetectedCount: number;
  totalEventsCount: number;
  detectedEventNames: string[];
  totalJobsSucceeded: number;
  totalJobsFailed: number;
  totalJobsRun: number;
  status: 'completed' | 'failed' | 'running';
  createdAt: string;
  recordId?: string;
}

const columnHelper = createColumnHelper<Invocation>();

interface InvocationsTableProps {
  correlationSearch?: string;
  timeRange?: string;
}

const InvocationsTable: React.FC<InvocationsTableProps> = ({ correlationSearch = '', timeRange: timeRangeOption = '24h' }) => {
  const navigate = useNavigate();
  const [selectedInvocation, setSelectedInvocation] = useState<Node | null>(null);
  const [hideZeroDetectedEvents, setHideZeroDetectedEvents] = useState(true);
  const [hideChildInvocations, setHideChildInvocations] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });

  // Resolve time range
  const resolved = useMemo(() => resolveTimeRange(timeRangeOption), [timeRangeOption]);

  // Build server-side where clause
  const where = useMemo(() => {
    const conditions: any[] = [
      { created_at: { _gte: resolved.start, _lte: resolved.end } },
    ];

    if (hideZeroDetectedEvents) {
      conditions.push({ events_detected_count: { _gt: 0 } });
    }

    if (hideChildInvocations) {
      conditions.push({ source_job_id: { _is_null: true } });
    }

    if (statusFilter !== 'all') {
      conditions.push({ status: { _eq: statusFilter } });
    }

    return { _and: conditions };
  }, [resolved.start, resolved.end, hideZeroDetectedEvents, hideChildInvocations, statusFilter]);

  // Reset to first page when filters change
  React.useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  }, [where, globalFilter]);

  const {
    data: queryData,
    loading,
    error,
    previousData,
  } = useInvocationsListQuery({
    variables: {
      limit: pagination.pageSize,
      offset: pagination.pageIndex * pagination.pageSize,
      where,
      order_by: [{ created_at: 'desc' as any }],
    },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  // Use previousData while loading to prevent flash
  const data = queryData || previousData;

  const totalCount = data?.invocations_aggregate?.aggregate?.count || 0;
  const pageCount = Math.ceil(totalCount / pagination.pageSize);

  const invocationsData = useMemo(() => {
    const invocations = data?.invocations || [];
    return invocations.map(inv => ({
      id: inv.id,
      sourceFunction: inv.source_function || '',
      correlationId: inv.correlation_id || '',
      userEmail: inv.source_user_email || 'system',
      sourceOperation: inv.source_operation || '',
      sourceJobId: inv.source_job_id || undefined,
      totalDuration: inv.total_duration_ms || 0,
      eventsDetectedCount: inv.detected_events?.aggregate?.count || inv.events_detected_count || 0,
      eventsUndetectedCount: inv.undetected_events?.aggregate?.count || 0,
      totalEventsCount: inv.event_executions_aggregate?.aggregate?.count || 0,
      detectedEventNames: inv.event_executions?.map(e => e.event_name).filter(Boolean) || [],
      totalJobsSucceeded: inv.total_jobs_succeeded || 0,
      totalJobsFailed: inv.total_jobs_failed || 0,
      totalJobsRun: inv.total_jobs_run || 0,
      status: inv.status as 'completed' | 'failed' | 'running',
      createdAt: inv.created_at,
      recordId: `${inv?.source_table?.split('.')[1]}:${(inv.source_event_payload as any)?.event?.data?.new?.id}`,
    }));
  }, [data]);

  // Update global filter when correlationSearch changes
  React.useEffect(() => {
    setGlobalFilter(correlationSearch);
  }, [correlationSearch]);

  const handleRowClick = (invocation: Invocation) => {
    navigate(`/flow?invocationId=${invocation.id}&autoFocus=true`);
  };

  const handleViewDetails = (invocation: Invocation, e: React.MouseEvent) => {
    e.stopPropagation();
    const node: Node = {
      id: invocation.id,
      type: 'invocation',
      position: { x: 0, y: 0 },
      data: {
        sourceFunction: invocation.sourceFunction,
        correlationId: invocation.correlationId,
        status: invocation.status,
        duration: invocation.totalDuration,
        eventsCount: invocation.eventsDetectedCount,
      },
    };
    setSelectedInvocation(node);
    setDrawerOpen(true);
  };

  const getStatusBadge = (status: string) => {
    const statusStyles = {
      completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      running: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    };
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          statusStyles[status as keyof typeof statusStyles]
        }`}
      >
        {status}
      </span>
    );
  };

  const getSuccessRate = (succeeded: number, total: number) => {
    if (total === 0) return 100;
    return Math.round((succeeded / total) * 100);
  };

  const getOperationColor = (operation: string) => {
    switch (operation.toUpperCase()) {
      case 'INSERT':
        return 'bg-green-500 text-white';
      case 'UPDATE':
        return 'bg-blue-500 text-white';
      case 'DELETE':
        return 'bg-red-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  const columns = useMemo<ColumnDef<Invocation, any>[]>(
    () => [
      columnHelper.accessor('sourceFunction', {
        id: 'sourceFunction',
        header: 'Source Function',
        cell: info => {
          const row = info.row.original;
          const operation = row.sourceOperation;

          return (
            <div className='relative inline-flex items-center gap-1.5 pl-1 pr-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full'>
              <div className='flex items-center justify-center w-6 h-6 bg-gray-700 dark:bg-gray-900 rounded-full flex-shrink-0'>
                <CircleStackIcon className='h-3.5 w-3.5 text-gray-100 dark:text-gray-300' />
              </div>
              <span className='font-medium text-gray-900 dark:text-gray-100 text-sm'>{info.getValue()}</span>
              {operation && (
                <span
                  className={`absolute -top-0.5 -right-0.5 px-1 py-0 rounded text-[8px] font-semibold uppercase leading-tight ${getOperationColor(
                    operation
                  )}`}
                >
                  {operation}
                </span>
              )}
            </div>
          );
        },
        filterFn: 'includesString',
      }),
      columnHelper.accessor('createdAt', {
        id: 'createdAt',
        header: 'Created',
        cell: info => (
          <div className='text-gray-600 dark:text-gray-400 text-sm'>{formatRelativeTime(info.getValue())}</div>
        ),
      }),
      columnHelper.accessor('totalDuration', {
        id: 'totalDuration',
        header: 'Duration',
        cell: info => {
          const ms = info.getValue();
          let displayValue: string;

          if (ms < 1000) {
            displayValue = `${ms}ms`;
          } else if (ms < 60000) {
            const seconds = (ms / 1000).toFixed(1);
            displayValue = `${seconds}s`;
          } else {
            const minutes = (ms / 60000).toFixed(1);
            displayValue = `${minutes}m`;
          }

          return <div className='text-gray-600 dark:text-gray-400 text-sm'>{displayValue}</div>;
        },
      }),
      columnHelper.display({
        id: 'events',
        header: 'Events',
        cell: info => {
          const row = info.row.original;
          const detected = row.eventsDetectedCount;
          const undetected = row.eventsUndetectedCount;
          const total = detected + undetected;

          return (
            <div className='flex items-center space-x-1'>
              {detected > 0 && (
                <span className='px-2 py-1 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 rounded'>
                  {detected}
                </span>
              )}
              {undetected > 0 && (
                <span className='px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded'>
                  {undetected}
                </span>
              )}
              {total === 0 && (
                <span className='px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded'>
                  0
                </span>
              )}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'jobs',
        header: 'Jobs',
        cell: info => {
          const row = info.row.original;
          return (
            <div className='flex items-center space-x-1'>
              <span className='px-2 py-1 text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400 rounded'>
                {row.totalJobsSucceeded}
              </span>
              {row.totalJobsFailed > 0 && (
                <span className='px-2 py-1 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 rounded'>
                  {row.totalJobsFailed}
                </span>
              )}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'successRate',
        header: 'Success Rate',
        cell: info => {
          const row = info.row.original;
          const successRate = getSuccessRate(row.totalJobsSucceeded, row.totalJobsRun);
          return (
            <div className='flex items-center space-x-2'>
              <span className='text-gray-900 dark:text-gray-100 font-medium text-sm'>{successRate}%</span>
              <div className='w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2'>
                <div
                  className={`h-2 rounded-full ${
                    successRate >= 90 ? 'bg-green-500' : successRate >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${successRate}%` }}
                />
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor('status', {
        id: 'status',
        header: 'Status',
        cell: info => getStatusBadge(info.getValue()),
        filterFn: 'equals',
      }),
      columnHelper.display({
        id: 'detectedEvents',
        header: 'Detected Events',
        cell: info => {
          const eventNames = info.row.original.detectedEventNames;
          if (eventNames.length === 0) return null;

          return (
            <div className='flex flex-wrap gap-0.5 max-w-xs'>
              {eventNames.map((eventName, idx) => (
                <span
                  key={idx}
                  className='inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono tracking-wide bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 whitespace-nowrap'
                >
                  {eventName}
                </span>
              ))}
            </div>
          );
        },
      }),
      columnHelper.accessor('recordId', {
        id: 'recordId',
        header: 'Record ID',
        cell: info => (
          <div className='text-gray-600 dark:text-gray-400 text-sm font-mono text-xs'>{info.getValue() || '-'}</div>
        ),
        filterFn: 'includesString',
      }),
      columnHelper.accessor('userEmail', {
        id: 'userEmail',
        header: 'User',
        cell: info => <div className='text-gray-600 dark:text-gray-400 text-sm'>{info.getValue()}</div>,
        filterFn: 'includesString',
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: info => (
          <div className='flex items-center space-x-2'>
            <button
              onClick={e => handleViewDetails(info.row.original, e)}
              className='p-1 text-gray-400 hover:text-blue-600 transition-colors'
              title='Quick Details'
            >
              <MagnifyingGlassIcon className='h-4 w-4' />
            </button>
            <ChevronRightIcon className='h-5 w-5 text-gray-400' title='View in Flow Diagram' />
          </div>
        ),
        size: 80,
      }),
    ],
    []
  );

  const table = useReactTable({
    data: invocationsData,
    columns,
    state: { columnVisibility, pagination },
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount,
    debugTable: false,
  });

  if (loading && !data) {
    return (
      <div className='h-full flex flex-col'>
        <div className='bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4'>
          <h2 className='text-xl font-semibold text-gray-900 dark:text-white'>Invocations</h2>
        </div>
        <div className='flex-1 flex items-center justify-center'>
          <div className='text-center'>
            <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
            <p className='mt-2 text-gray-600 dark:text-gray-400'>Loading invocations...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='h-full flex flex-col'>
        <div className='bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4'>
          <h2 className='text-xl font-semibold text-gray-900 dark:text-white'>Invocations</h2>
        </div>
        <div className='flex-1 flex items-center justify-center'>
          <div className='text-center'>
            <ExclamationTriangleIcon className='mx-auto h-12 w-12 text-yellow-500 mb-4' />
            <p className='text-yellow-600 dark:text-yellow-400 mb-2'>Invocations data unavailable</p>
            <p className='text-sm text-gray-600 dark:text-gray-400'>The observability database may not be connected.</p>
            <p className='text-xs text-gray-500 dark:text-gray-500 mt-2'>{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='h-full flex flex-col'>
      {/* Header */}
      <div className='bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4'>
        <div className='flex items-center justify-between'>
          <div>
            <h2 className='text-xl font-semibold text-gray-900 dark:text-white'>Invocations</h2>
            <p className='mt-1 text-sm text-gray-600 dark:text-gray-400'>
              {totalCount.toLocaleString()} invocations{loading ? ' (updating...)' : ''}
            </p>
          </div>

          <div className='flex items-center space-x-3'>
            <div className='relative'>
              <MagnifyingGlassIcon className='h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400' />
              <input
                value={globalFilter ?? ''}
                onChange={e => setGlobalFilter(String(e.target.value))}
                className='pl-10 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500'
                placeholder='Search all fields...'
              />
            </div>

            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className='px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100'
            >
              <option value='all'>All Status</option>
              <option value='completed'>Completed</option>
              <option value='failed'>Failed</option>
              <option value='running'>Running</option>
            </select>

            <label className='inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600'>
              <input
                type='checkbox'
                checked={!hideZeroDetectedEvents}
                onChange={e => setHideZeroDetectedEvents(!e.target.checked)}
                className='h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500'
              />
              <span className='ml-2 text-sm text-gray-700 dark:text-gray-200'>Show Undetected Events</span>
            </label>

            <label className='inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600'>
              <input
                type='checkbox'
                checked={hideChildInvocations}
                onChange={e => setHideChildInvocations(e.target.checked)}
                className='h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500'
              />
              <span className='ml-2 text-sm text-gray-700 dark:text-gray-200'>Hide Child Events</span>
            </label>

            <button className='inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-lg text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600'>
              <AdjustmentsHorizontalIcon className='h-4 w-4 mr-2' />
              Columns
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div
        className='flex-1 overflow-auto bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700'
        style={{ scrollbarGutter: 'stable both-edges' }}
      >
        {/* Scoped fix: remove any ::before/::after pseudo-elements on rows */}
        <style>{`
          /* Scope by data attribute so we don't affect other tables */
          [data-inv-table] tbody tr::before,
          [data-inv-table] tbody tr::after {
            content: none !important;
            display: none !important;
          }
        `}</style>

        <table className='w-full' data-inv-table>
          <thead className='bg-gray-50 dark:bg-gray-700/50'>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className='sticky top-0 z-10 bg-gray-50 dark:bg-gray-700/50 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody className='bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700'>
            {table.getRowModel().rows.map(row => (
              <motion.tr
                key={row.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={() => handleRowClick(row.original)}
                className='hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors'
              >
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id} className='px-6 py-4 whitespace-nowrap text-sm'>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </motion.tr>
            ))}
            {table.getRowModel().rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className='px-6 py-8 text-center text-gray-500 dark:text-gray-400'>
                  No invocations found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className='bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-3'>
        <div className='flex items-center justify-between'>
          <div className='text-sm text-gray-700 dark:text-gray-300'>
            Showing{' '}
            <span className='font-medium'>
              {totalCount > 0 ? pagination.pageIndex * pagination.pageSize + 1 : 0}
            </span>{' '}
            to{' '}
            <span className='font-medium'>
              {Math.min((pagination.pageIndex + 1) * pagination.pageSize, totalCount)}
            </span>{' '}
            of <span className='font-medium'>{totalCount.toLocaleString()}</span> results
          </div>
          <div className='flex items-center space-x-2'>
            <select
              value={table.getState().pagination.pageSize}
              onChange={e => {
                table.setPageSize(Number(e.target.value));
              }}
              className='px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100'
            >
              {[25, 50, 100, 200].map(pageSize => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className='px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              Previous
            </button>
            <span className='text-sm text-gray-700 dark:text-gray-300'>
              Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
            </span>
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className='px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {drawerOpen && selectedInvocation && (
          <>
            {selectedInvocation.type === 'invocation' && (
              <InvocationDetailDrawer
                node={selectedInvocation}
                isOpen={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                onSelectNode={(node) => setSelectedInvocation(node)}
              />
            )}
            {selectedInvocation.type === 'event' && (
              <EventDetailDrawer
                node={selectedInvocation}
                isOpen={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                onSelectNode={(node) => setSelectedInvocation(node)}
              />
            )}
            {selectedInvocation.type === 'job' && (
              <JobDetailDrawer
                node={selectedInvocation}
                isOpen={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                onSelectNode={(node) => setSelectedInvocation(node)}
              />
            )}
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InvocationsTable;
